// react
import { useMemo } from 'react';

// fontawesome
import { SizeProp } from '@fortawesome/fontawesome-svg-core';

// icons — side-effect import ensures library.add() runs before any Icon renders
import '@/components/atoms/Icon/icons';
import type { Icons } from '@/components/atoms/Icon/icons';
import { Circle, StyledFontAwesomeIcon } from '@/components/atoms/Icon/parts';

// theme
import { sp } from '@/theme/mui';
import { Color } from '@/theme/color';

export type IconType = 'fas' | 'fal' | 'fab';

export interface IconProps {
  className?: string;
  icon: Icons;
  type?: IconType;
  size?: SizeProp;
  fontSize?: number;
  isFlip?: boolean;
  fill?: Color;
  color?: string;
  isElevation?: boolean;
}

/**
 * Icon displays an icon.
 */
export const Icon = ({
  className,
  icon,
  size,
  fontSize,
  isFlip,
  color,
  type = 'fal',
  fill,
  isElevation,
}: IconProps) => {
  // memos
  const Wrapper = useMemo(() => {
    return ({ children, ...props }: any) => {
      if (fill) {
        return <Circle {...props}>{children}</Circle>;
      }
      if (color) {
        return (
          <div style={{ ...(props?.style || {}), color }} {...props}>
            {children}
          </div>
        );
      }
      return <>{children}</>;
    };
  }, [fill, color]);

  const padding = useMemo(() => {
    let padding = '16px';
    switch (size) {
      case '2xs':
      case 'xs':
        padding = sp[4];
        break;
      case 'sm':
        padding = sp[8];
        break;
      case 'lg':
        padding = sp[12];
        break;
      case 'xl':
        padding = sp[16];
        break;
      case '2xl':
      case '2x':
        padding = sp[20];
        break;
      case '1x':
        padding = sp[8];
        break;
      case '3x':
        padding = sp[24];
        break;
      case '4x':
        padding = '36px';
        break;
      case '5x':
      case '6x':
      case '7x':
      case '8x':
      case '9x':
      case '10x':
        padding = sp[48];
        break;
      default:
        padding = sp[8];
        break;
    }

    if (fontSize) {
      if (fontSize < 12) {
        padding = sp[4];
      } else if (fontSize < 24) {
        padding = sp[8];
      } else if (fontSize < 32) {
        padding = sp[12];
      } else if (fontSize < 44) {
        padding = sp[16];
      } else if (fontSize < 64) {
        padding = sp[20];
      } else if (fontSize < 82) {
        padding = sp[24];
      } else if (fontSize < 112) {
        padding = '36px';
      } else if (fontSize < 192) {
        padding = sp[48];
      } else if (fontSize >= 192) {
        padding = sp[64];
      }
    }
    return padding;
  }, [size, fontSize]);

  const props = useMemo(() => {
    if (!fill) return {};
    return {
      $color: fill,
      $padding: padding,
      $isElevation: isElevation,
    };
  }, [fill, padding, isElevation]);

  return (
    <Wrapper {...props}>
      {fill && (
        <StyledFontAwesomeIcon
          className={className}
          icon={['fal', 'star']}
          size={size}
          fontSize={fontSize}
          $isHidden
        />
      )}
      <StyledFontAwesomeIcon
        className={className}
        icon={[type, icon]}
        flip={isFlip ? 'horizontal' : undefined}
        size={size}
        fontSize={fontSize}
        $isFill={!!fill}
      />
    </Wrapper>
  );
};

Icon.displayName = 'Icon';

export default Icon;
